import React, { useEffect, useContext, useState } from "react";
import { AppContext } from "../../../Context/AppContext";
import { useNavigate } from "react-router";
import ServiceWrapper from "../../../Services/ServiceWrapper";
import AppSettings from "../../../Settings/AppSettings";
import AuthManager from "../../../Common/AuthManager";
import { AttributeContext } from "./AttributeContext";
import Utils from "../../../Common/Utils";
import SkeletonElement from "../Category/Shimmer/SkeletonElement";
import { useInfiniteScroll } from "../../../Pages/GMS/Mapping/hooks/useInfiniteScroll";
import Search from "Components/DesignSystem/Search/Search";
import Alert from "Components/DesignSystem/Alert/Alert";
import Spinner from "Components/DesignSystem/Spinner/Spinner";
import EmptyState from "Components/DesignSystem/EmptyState/EmptyState";
import CustomAttributeTag from "Components/Common/CustomAttributeTag";

// The attribute list is paginated server-side because QA/prod industries hold far more
// attributes than a single response can return quickly. 200 keeps the number of scroll
// fetches low, which matters here: the backend cost is dominated by a fixed ~700ms per
// request (Bolt driver setup), not by row count, so fewer/larger pages beat many small ones.
const ATTRIBUTES_PER_PAGE = 200;
// Search is now resolved server-side, so it must be debounced — every keystroke would
// otherwise cost a full round trip.
const SEARCH_DEBOUNCE_MS = 350;

let lastSelectedAttribute = 0;
function AttrList(props) {
  const navigate = useNavigate();
  let AttributeContextData = useContext(AttributeContext);
  let appData = useContext(AppContext);
  let industryId =
    appData.get_selected_industry() && appData.get_selected_industry().id;

  let [loading, setLoading] = React.useState(false);
  let [attributeList, setAttributeList] = React.useState([]);
  let [errorMessage, setErrorMessage] = React.useState(null);
  let [filteredAttributeList, setFilteredAttributeList] =
    useState(attributeList);

  // Pagination / server-side search state.
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  // DS `Search` is controlled, where the legacy `<input>` was not. `searchTerm` holds the
  // TRIMMED term the fetches use (unchanged); this holds the raw text as typed, so a
  // trailing space is not eaten out from under the user mid-keystroke.
  const [searchInputValue, setSearchInputValue] = useState("");
  /*
    § 288 — the debounce window. `searching` below only goes true when the request
    starts, which is SEARCH_DEBOUNCE_MS after the keystroke the user is watching;
    `searchTerm` is set immediately, so the two can never express the gap between
    them. Presentational, and deliberately NOT `searching` itself — that one also
    guards `loadMore`, so widening it would change when pages load.
  */
  const [searchPending, setSearchPending] = useState(false);
  const searchDebounceRef = React.useRef(null);
  // The created id the list has already reloaded for. AddEditViewAttr announces a create by
  // setting BOTH newAttributeId and listReloadCounter in the same handler, so the two effects
  // below fire in the same commit and used to issue two list requests that raced (see the
  // listReloadCounter effect). newAttributeId is only ever reset on delete, so "pending" has to
  // be "differs from the last one handled", not "non-empty".
  const handledNewAttributeIdRef = React.useRef("");
  // Appending a page reads the current list inside an IntersectionObserver callback, which
  // captures a stale closure; the ref gives that path the committed value.
  const attributeListRef = React.useRef([]);

  React.useEffect(() => {
    attributeListRef.current = attributeList;
  }, [attributeList]);

  React.useEffect(
    () => () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    },
    []
  );

  async function load_Attributes(newAttributeFound = false, opts = {}) {
    const {
      page: targetPage = 1,
      append = false,
      search = searchTerm,
      viaSearch = false,
    } = opts;

    if (append) {
      setPaginationLoading(true);
    } else if (viaSearch) {
      // Deliberately does NOT set `loading` — that unmounts the search input and the user
      // loses focus mid-typing.
      setSearching(true);
    } else {
      setLoading(true);
      lastSelectedAttribute = 0;
    }
    if (!append) {
      AttributeContextData?.setUpdatedAttributeId('');
    }

    let api_base_url = AppSettings.GMS_BACKEND_API_URL;
    const params = new URLSearchParams({
      page: String(targetPage),
      limit: String(ATTRIBUTES_PER_PAGE),
    });
    if (search) {
      params.set("search", search);
    }
    let ret = await ServiceWrapper.doGet(
      `${api_base_url}industry/${industryId}/attribute/list?${params.toString()}`,
      null,
      null,
      AuthManager.getGmsToken()
    );

    if (!ret.errorfound) {
      setHasMore(Boolean(ret.data.pagination && ret.data.pagination.has_more));
      setPage(targetPage);

      // A page appended by scrolling must not disturb the current selection or detail pane.
      if (append) {
        const appended = ret.data.data.map((attribute) => ({
          ...attribute,
          showCopyIcon: false,
        }));
        const merged = [...attributeListRef.current, ...appended];
        // The URL's attribute can live on a page the initial load never fetched, so nothing was
        // highlighted then. Claim it as soon as its page arrives — but only while no row is
        // highlighted, so a row the user has clicked in the meantime keeps the selection.
        const appendUid = props.props.props?.match?.params?.uid;
        if (appendUid && !merged.some((attribute) => attribute.showCopyIcon)) {
          const uidIndex = merged.findIndex((attribute) => attribute.id === appendUid);
          if (uidIndex >= 0) {
            merged[uidIndex].showCopyIcon = true;
            lastSelectedAttribute = uidIndex;
          }
        }
        attributeListRef.current = merged;
        setAttributeList(merged);
        setFilteredAttributeList(merged);
        setPaginationLoading(false);
        return;
      }

      // A search replaces the list wholesale and leaves the selected attribute alone.
      if (viaSearch) {
        const results = ret.data.data.map((attribute) => ({
          ...attribute,
          showCopyIcon: false,
        }));
        // Must reset: lastSelectedAttribute indexes into the list, and a server-side search
        // can return a shorter list than the index the user last clicked. Under the previous
        // client-side search this was masked, because a filtered list made
        // filteredAttributeList.length !== attributeList.length and updateAttributeDetailPage
        // took its map-based branch instead of indexing by lastSelectedAttribute.
        //
        // Reset to -1, not 0: every result row is rendered unselected (showCopyIcon: false), and
        // updateAttributeDetailPage ignores a click whose index equals lastSelectedAttribute
        // ("already selected"). Resetting to 0 therefore made the FIRST search result unclickable
        // until some other row had been clicked (AB#14436). -1 matches no row, and both index
        // reads in updateAttributeDetailPage are bounds-checked, so attributes[-1] is never
        // written. A fresh (non-search) load overwrites this with 0 before the newAttributeId
        // sentinel check runs, so the two uses of -1 cannot collide.
        lastSelectedAttribute = -1;
        attributeListRef.current = results;
        setAttributeList(results);
        setFilteredAttributeList(results);
        setErrorMessage(null);
        setSearching(false);
        return;
      }

      // No client-side re-sort: the backend now orders by toLower(name), which is what the
      // previous localeCompare sort produced. Re-sorting accumulated pages could reorder
      // items across a page boundary and make the list visibly jump while scrolling.
      const sortedData = ret.data.data;
      AttributeContextData.setAttributeId(sortedData[0]?.id);
      let attributes = sortedData;
      if (attributes.length > 0) {
        attributes = attributes.map((attribute) => ({
          ...attribute,
          showCopyIcon: false,
        }));
        // The highlighted row must follow the URL, not the index the user last clicked.
        //
        // lastSelectedAttribute is a module-level index reset to 0 at the top of every fresh
        // load, so arriving at /gms/attributes/<uid> -- from the global search results page, a
        // bookmark, or a reload -- highlighted row 0 while the detail pane loaded <uid> from
        // `editingItem`. The list and the pane disagreed unless <uid> happened to be the first
        // attribute. Resolving the index by id is the only thing that keeps them in step; the
        // URL is the trustworthy source here, because on mount setEditingItem and this load run
        // in the same tick and `AttributeContextData.editingItem` is still the stale closure value.
        const urlUid = props.props.props?.match?.params?.uid;
        let highlightIndex = lastSelectedAttribute;
        if (lastSelectedAttribute === -1) {
          const newIndex = attributes.findIndex((e) => e.id === AttributeContextData.newAttributeId);
          // findIndex returns -1, never null, so the old `?? 0` could not catch a miss and the
          // write below indexed attributes[-1].
          highlightIndex = newIndex >= 0 ? newIndex : 0;
          lastSelectedAttribute = highlightIndex;
        } else if (urlUid && urlUid !== "0") {
          // A miss (-1) means the uid lives on a page not fetched yet. Highlight nothing rather
          // than falling back to row 0: a blue row next to a detail pane showing a different
          // attribute is the exact confusion this fix exists to remove. lastSelectedAttribute is
          // deliberately left alone on a miss — -1 is its "new attribute" sentinel above.
          highlightIndex = attributes.findIndex((e) => e.id === urlUid);
          if (highlightIndex >= 0) {
            lastSelectedAttribute = highlightIndex;
          }
        }
        if (attributes[highlightIndex]) {
          attributes[highlightIndex].showCopyIcon = true;
        }
      } else {
        setLoading(false);
      }

      if (
        props.props.props.match.params.uid === undefined &&
        AttributeContextData.editingItem === null
      ) {
        if (sortedData.length > 0) {
          AttributeContextData.setEditingItem({
            form: "view",
            id: sortedData[0].id,
          });
          setAttributeList(attributes);
          setFilteredAttributeList(attributes);
          setLoading(false);
        }
      } else {
        setAttributeList(attributes);
        setFilteredAttributeList(attributes);
        setLoading(false);
        if (newAttributeFound) {
          const index = attributes.findIndex(
            (obj) => obj.id === AttributeContextData.newAttributeId
          );

          if (index === -1) {
            // The new attribute sorts beyond the page just fetched (page 1 only). The fresh-load
            // path above has already highlighted row 0; undo that — a blue row next to a pane
            // showing a different attribute is the confusion §5.4 of okf/attribute/e2e.md exists
            // to prevent. -1 matches no row, so the next click on any row (row 0 included) lands.
            attributes.forEach((attribute) => {
              attribute.showCopyIcon = false;
            });
            lastSelectedAttribute = -1;
          } else {
            updateAttributeDetailPage(index, attributes[index], attributes, newAttributeFound);
          }
        }

        if (AttributeContextData.updatedAttributeId !== "") {
          const index = attributes.findIndex(
            (obj) => obj.id === AttributeContextData.updatedAttributeId
          );
          const item = attributes[index];
          updateAttributeDetailPage(index, item, null, false);
        }
      }

      if (window.location.pathname === "/gms/attributes/0") {
        if (
          AttributeContextData?.editingItem?.id === undefined ||
          AttributeContextData?.editingItem?.id === "0"
        ) {
          if (AttributeContextData?.newAttributeId) {
            AttributeContextData.setEditingItem({
              form: "view",
              id: "AttributeContextData?.newAttributeId",
            });
          } else {
            if (AttributeContextData.attributeDeleted) {
              AttributeContextData.setEditingItem({
                form: "view",
                id: sortedData[0]?.id ?? "",
              });
              AttributeContextData.setAttributeDeleted(false);
            } else {
              AttributeContextData.setEditingItem(null);
              setTimeout(() => {
                AttributeContextData.setEditingItem({
                  form: "edit",
                  id: "0",
                });
              }, 1000);
            }
          }
        } else {
          AttributeContextData.setEditingItem({
            form: "view",
            id: sortedData[0]?.id ?? "",
          });
        }
      }
    } else {
      setLoading(false);
      setErrorMessage("Error While Loading Attributes");
    }
    // Fall back to the URL's attribute only when the URL actually carries one.
    //
    // This used to fire whenever `props.props.props` merely existed. On /gms/attributes there is no
    // uid, so it overwrote the selection made above with {form:"view", id: undefined} -- in the same
    // tick -- and the detail pane had no id to load. The `!editingItem` guard did not stop it:
    // that is the value captured in this closure, still null after the setEditingItem above.
    if (
      !AttributeContextData.editingItem &&
      props.props.props?.match?.params?.uid
    ) {
      AttributeContextData.setEditingItem({
        form: "view",
        id: props.props.props.match.params.uid,
      });
    }
    AttributeContextData.setUpdatedAttributeId("");
  }

  function updateAttributeDetailPage(
    index,
    item,
    resAttributes,
    newAttributeFound
  ) {
    if (filteredAttributeList.length !== attributeList.length) {
      // update filter list
      const attributes = filteredAttributeList.map((attribute) =>
        attribute.id === item.id
          ? { ...attribute, showCopyIcon: !attribute.showCopyIcon }
          : { ...attribute, showCopyIcon: false }
      );
      AttributeContextData.setAttributeId(item?.id ?? "");
      AttributeContextData.setEditingItem({ form: "view", id: item?.id ?? "" });
      setFilteredAttributeList(attributes);

      // update attribute list
      const attrList = attributeList.map((attribute) =>
        attribute.id === item.id
          ? { ...attribute, showCopyIcon: !attribute.showCopyIcon }
          : { ...attribute, showCopyIcon: false }
      );
      const attrIndex = attrList.findIndex(
        (attribute) => attribute.id === item.id
      );
      setAttributeList(attrList);
      lastSelectedAttribute = attrIndex;
    } else {
      const attributes = newAttributeFound
        ? resAttributes
        : filteredAttributeList;
      if (index !== lastSelectedAttribute && item) {
        AttributeContextData.setAttributeId(item?.id ?? "");
        AttributeContextData.setEditingItem({
          form: "view",
          id: item?.id ?? "",
        });
        // Both indexes are bounds-checked because the list length is no longer fixed for the
        // lifetime of the screen — a search shrinks it and a scrolled page grows it, so a
        // previously valid index can fall outside the current array.
        if (attributes[index]) {
          attributes[index].showCopyIcon = !item.showCopyIcon;
        }
        if (attributes[lastSelectedAttribute]) {
          attributes[lastSelectedAttribute].showCopyIcon = false;
        }
        setFilteredAttributeList(attributes);
        lastSelectedAttribute = index;
      }
    }
  }

  useEffect(() => {
    if (appData.pm.checkPermission("gmsattributes_read")) {
      if (props.props.props && props.props.props.match.params.uid) {
        AttributeContextData.setEditingItem({
          form: "view",
          id: props.props.props.match.params.uid,
        });
      }
      if (industryId) {
        // A create bumps listReloadCounter AND sets newAttributeId in one handler. The
        // newAttributeId effect below owns that reload (unfiltered, highlighting the new row);
        // issuing a second one here — with the stale search term still in this closure — made
        // the two responses race, and when this one landed last the list kept showing the old
        // search results with row 0 highlighted while the pane showed the new attribute
        // (AB#14464). Edits bump the counter without changing newAttributeId and still reload.
        const createPending =
          AttributeContextData.newAttributeId !== "" &&
          AttributeContextData.newAttributeId !== handledNewAttributeIdRef.current;
        if (!createPending) {
          load_Attributes(false);
        }
      }
    } else {
      navigate("/workspace/module/permission/denied");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [industryId, AttributeContextData.listReloadCounter]);

  useEffect(() => {
    if (AttributeContextData.newAttributeId !== "") {
      // A freshly created attribute is not, in general, a match for whatever the user last
      // searched, so keeping the filter would hide the very row the reload exists to highlight.
      // Clear the search and reload page 1 unfiltered (AB#14464).
      //
      // TWO pieces of state, not one. This used to say "the input is controlled by it"
      // about `searchTerm`, and that was true while the box was a native input. The
      // design-system pass split the box's own text into `searchInputValue` (so the field
      // stays responsive while the debounced `searchTerm` lags behind it), which left this
      // clearing the QUERY but not the FIELD: the reload came back unfiltered while the box
      // still showed the old term, i.e. a list that visibly disagreed with its own filter.
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      setSearchTerm("");
      setSearchInputValue("");
      setSearchPending(false);
      handledNewAttributeIdRef.current = AttributeContextData.newAttributeId;
      load_Attributes(true, { search: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [AttributeContextData.newAttributeId]);

  useEffect(() => {
    if (AttributeContextData.updatedAttributeId !== "") {
      load_Attributes(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [AttributeContextData.updatedAttributeId]);

  // Search resolves server-side now. Filtering only the loaded array would silently hide a
  // match that lives on a page the user has not scrolled to yet.
  function handleSearchAttribute(value) {
    const term = Utils.isEmpty(value) ? "" : value.toString().trim();
    setSearchTerm(term);
    setSearchPending(true);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setSearchPending(false);
      load_Attributes(false, {
        page: 1,
        append: false,
        search: term,
        viaSearch: true,
      });
    }, SEARCH_DEBOUNCE_MS);
  }

  const loadMore = React.useCallback(() => {
    if (paginationLoading || searching || loading || !hasMore) {
      return;
    }
    load_Attributes(false, {
      page: page + 1,
      append: true,
      search: searchTerm,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationLoading, searching, loading, hasMore, page, searchTerm]);

  const sentinelRef = useInfiniteScroll(loadMore, hasMore, paginationLoading);

  useEffect(() => {
    if (appData.editingItem) {
      AttributeContextData.setEditingItem({
        form: "edit",
        id: "0",
      });
      appData.setEditingItem(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appData.editingItem]);

  return (
    <React.Fragment>
      {loading && (
        <div className="pe-3 ps-0">
          <SkeletonElement type={"title"} />
          <br />
          <SkeletonElement type={"text"} />
          <SkeletonElement type={"text"} />
          <SkeletonElement type={"text"} />
          <SkeletonElement type={"text"} />
          <SkeletonElement type={"text"} />
          <SkeletonElement type={"text"} />
        </div>
      )}

      {!loading && errorMessage && (
        <Alert tone="danger" live className="tw-m-2" data-testid="attribute-list-error-alert">
          {errorMessage}
        </Alert>
      )}
      {!loading && !errorMessage && (
        <>
          <div className="top-0 left-0 col h-100 md:overflow-y-auto w-100 pe-2 md:max-h-full scrollbar-none">
            <div className="flex-shrink-0">
              {/* No stray `&nbsp;` after the box — the same phantom whitespace the
                  category list dropped when it took DS Search. */}
              <div className="p-0 pb-0">
                {(attributeList.length > 0 || searchTerm) && (
                  <Search
                    data-testid="attribute-list-search-input"
                    placeholder="Search ..."
                    ariaLabel="Search attributes"
                    size="sm"
                    value={searchInputValue}
                    onChange={(value) => {
                      setSearchInputValue(value);
                      handleSearchAttribute(value);
                    }}
                    /* § 288 — `searching` is the search fetch (already distinct from
                       `paginationLoading`, the infinite-scroll one); `searchPending`
                       is the debounce in front of it. */
                    loading={searching || searchPending}
                    loadingLabel="Searching attributes"
                    className="tw-my-2"
                  />
                )}
              </div>
              <div
                className="mt-0 "
                style={{
                  //maxHeight: "80vh",
                  overflow: "scroll",
                  paddingRight: "5px",
                }}
              >
                {!loading &&
                  (attributeList.length > 0 ? (
                    <ul className="list-unstyled">
                      {filteredAttributeList.map((item, index) => {
                        return (
                          <li key={index}>
                            {/* Off the Bootstrap collapse plumbing (it pointed at a target
                                that never existed) and the inline #091caa/#8D8D8D colours.
                                The selected row wears the § 49 selection pair (accent ink
                                on accent tint), the same grammar as the category tree; the
                                detail-load block stays visible — dimmed, not-allowed —
                                rather than a silent no-op. */}
                            <span role="button" data-testid={`attribute-attrlist-span-1-${index}`}
                              aria-disabled={AttributeContextData.detailLoading || undefined}
                              data-selected={item.showCopyIcon || undefined}
                              onClick={() => {
                                // One detail load at a time. Clicking through a pending load
                                // stacks up fetches for different attributes, and whichever
                                // resolves last wins -- so the pane can end up showing an
                                // attribute other than the row that looks selected.
                                if (AttributeContextData.detailLoading) return;
                                updateAttributeDetailPage(
                                  index,
                                  item,
                                  null,
                                  false
                                );
                              }}
                              className={`tw-flex tw-w-full tw-items-center tw-rounded-ctl tw-px-2 tw-py-0.5 tw-mt-0.5 tw-transition-colors ${
                                AttributeContextData.detailLoading
                                  ? "tw-cursor-not-allowed tw-opacity-60"
                                  : "tw-cursor-pointer"
                              } ${
                                item.showCopyIcon
                                  ? "tw-bg-accent-tint"
                                  : "hover:tw-bg-surface-hover"
                              }`}
                            >
                              {/* Unselected rows are QUIET (regular weight, secondary
                                  ink) so the selection carries the emphasis — full ink
                                  at SemiBold made all 100+ rows dominate the pane
                                  (user-reported 2026-09-01; legacy rows were grey). */}
                              <label
                                title={item.name}
                                className={`tw-truncate tw-mb-0 tw-min-w-0 tw-max-w-[90%] ${
                                  item.showCopyIcon
                                    ? "tw-text-sub-title-label tw-text-accent"
                                    : "tw-text-caption-metadata tw-text-ink-secondary"
                                }`}
                              >
                                {item.name}
                              </label>
                              {/* AB#14524: custom attributes carry the tag glyph to the
                                  RIGHT of the name, snug against the text -- the label is
                                  max-w-[90%] (not w-[90%]) so it shrinks to its content and
                                  still truncates when long; hover reads "Custom attribute". The
                                  list API projects is_custom via coalesce(), so a missing
                                  key on older payloads simply means not custom. */}
                              {item.is_custom === true && (
                                <CustomAttributeTag
                                  className="tw-ml-1"
                                  data-testid={`attribute-attrlist-custom-tag-${index}`}
                                />
                              )}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <div className="tw-flex tw-justify-center tw-py-4">
                      <EmptyState
                        size="sm"
                        title="No Attributes found"
                        data-testid="attribute-list-empty-state"
                      />
                    </div>
                  ))}
                {hasMore && (
                  <div
                    ref={sentinelRef}
                    data-testid="attribute-list-scroll-sentinel"
                    style={{ height: "20px", width: "100%" }}
                  />
                )}
                {(paginationLoading || searching) && (
                  <div
                    className="py-2 tw-flex tw-justify-center"
                    data-testid="attribute-list-pagination-loading"
                  >
                    <Spinner size="sm" label="Loading attributes" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </React.Fragment>
  );
}

export default AttrList;
